import { Box } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { SnackbarProvider } from 'notistack'
import * as React from 'react'
import { Navigate, Route, Routes } from 'react-router'

// Components
import SimpleBottomNavigation from './components/common/BottomNavigation'
import TopNavigation from './components/common/TopNavigation'
import WorkInProgress from './components/common/WorkInProgress'
import GroupSessionCreate from './components/forms/GroupSessionCreate'
import PrivateSessionCreate from './components/forms/PrivateSessionCreate'
import Signup from './components/forms/Signup'
import AppContent from './components/layout/AppContent'
import { AuthProvider } from './contexts/AuthContext'
import { GroupSessionProvider } from './contexts/GroupSessionContext'
import { SessionProvider } from './contexts/PrivateSessionContext'
import { TripProvider } from './contexts/TripContext'
import { UserProvider } from './contexts/UserContext'
import Config from './data/Config'
import { useChatInitialization } from './domains/chat/useChatInitialization'
// Pages
import AtomDevDemo from './pages/AtomDevDemo'
import Booking from './pages/Booking'
import ChatPage from './pages/ChatPage'
import Dashboard from './pages/Dashboard'
import DevContactCard from './pages/DevContactCard'
import Demo from './pages/DevDemo'
import DemoComp from './pages/DevDemoComp'
import DevEditor from './pages/DevEditor'
import DevInstructor from './pages/DevInstructor'
import DevMedia from './pages/DevMedia'
import DevPage from './pages/DevPage'
import DevTheme from './pages/DevTheme'
import DevUserCard from './pages/DevUserCard'
import Events from './pages/Events'
import FeatureDevMapPage from './pages/FeatureDevMapPage'
import GroupSessionEditPage from './pages/GroupSessionEditPage'
import GroupSessions from './pages/GroupSessions'
import GroupSessionView from './pages/GroupSessionView'
import History from './pages/History'
import Home from './pages/Home'
import Instructors from './pages/Instructors'
import InstructorView from './pages/InstructorView'
import Login from './pages/Login'
import ProfilePage from './pages/ProfilePage'
import ProtectedRoute from './pages/ProtectedRoute'
import ResetPassword from './pages/ResetPassword'
import SiteMap from './pages/SiteMap'
import Trips from './pages/Trips'
import TripView from './pages/TripView'
import UnauthorizedPage from './pages/UnauthorizedPage'
import Users from './pages/Users'
import UserView from './pages/UserView'
// Contexts
import { ROLES } from './share/Constant'
// Styles
import './App.css'

const notistackRef = React.createRef()
const runChatInitialization = false

// import { DevTools } from 'jotai-devtools'

/**
 * Internal component that initializes chat data when user is logged in
 * This ensures unread message counts are displayed even if user hasn't visited chat page
 */
function ChatInitializer() {
  console.log('ChatInitializer')
  useChatInitialization()
  return null
}

function App() {
  // This caused extra render, disable it for now
  // const navigate = useNavigate()
  // React.useEffect(() => {
  //   const handleAuthError = (event) => {
  //     // Navigate to login page when auth error occurs
  //     navigate('/login', {
  //       state: {
  //         message: event.detail === 'TOKEN_EXPIRED' ? 'Session expired, please login again' : 'Please login first',
  //       },
  //     })
  //   }

  //   window.addEventListener('AUTH_ERROR', handleAuthError)
  //   return () => window.removeEventListener('AUTH_ERROR', handleAuthError)
  // }, [navigate])

  const googleClientId = Config.GOOGLE_AUTH_CLIENT_ID
  if (!googleClientId) {
    console.error('Missing REACT_APP_CLIENT_GOOGLE_AUTH_CLIENT_ID environment variable')
  }

  console.log('RenderApp')
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      {/* <DevTools /> */}
      <AuthProvider>
        <UserProvider>
          <SessionProvider>
            <GroupSessionProvider>
              <TripProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <SnackbarProvider
                    maxSnack={3}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    autoHideDuration={2000}
                    dense
                    ref={notistackRef}
                    SnackbarProps={{
                      onClick: (event, reason, key) => {
                        // 使用 ref 方式关闭，或者直接移除这个 onClick
                        // notistack 默认点击就会关闭，所以可以移除这个处理
                        notistackRef.current.closeSnackbar(key)
                      },
                    }}
                  >
                    {/* TODO, this casued more query */}
                    {runChatInitialization && <ChatInitializer />}
                    <TopNavigation />
                    <Box
                      className="App"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <AppContent>
                        <Routes>
                          <Route element={<ProtectedRoute allowAnonymous={true} />}>
                            <Route path="/" element={<Home />} />
                            <Route path="/booking" element={<Booking />} />
                            <Route path="/events/sessions" element={<GroupSessions />} />
                            <Route path="/events/sessions/:id" element={<GroupSessionView />} />
                            <Route path="/events/sessions/:id/edit" element={<GroupSessionEditPage />} />
                            <Route path="/events/trips" element={<Trips />} />
                            <Route path="/events/trips/:id" element={<TripView />} />
                            <Route path="/events" element={<Events />} />
                            <Route path="/instructors" element={<Instructors />} />
                            <Route path="/instructors/:id" element={<InstructorView />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                          </Route>

                          {/* 需要登入 */}
                          <Route element={<ProtectedRoute />}>
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/mysessions" element={<History />} />
                            {Config.CHAT_ENABLED && <Route path="/chat" element={<ChatPage />} />}

                            {/* // A workaround for the history page */}
                            <Route path="/history" element={<Navigate to="/mysessions" replace />} />
                            <Route path="/instructor/sessions" element={<Navigate to="/mysessions" replace />} />
                          </Route>

                          <Route path="/signup" element={<Signup />} />
                          <Route path="/sitemap" element={<SiteMap />} />

                          <Route path="/feature-dev-map" element={<FeatureDevMapPage />} />

                          <Route element={<ProtectedRoute allowedRoles={[ROLES.INSTRUCTOR]} />}>
                            <Route path="/private-session/create" element={<PrivateSessionCreate />} />
                            <Route path="/group-session/create" element={<GroupSessionCreate />} />
                          </Route>

                          {/* 需要登入且角色為 admin 才可訪問：Admin */}
                          <Route element={<ProtectedRoute allowedRoles={[ROLES.ADMIN]} />}>
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/users" element={<Users />} />
                            <Route path="/users/:id" element={<UserView />} />
                            <Route path="/demo" element={<Demo />} />
                            <Route path="/dev" element={<DevPage />} />
                            <Route path="/dev/demo" element={<Demo />} />
                            <Route path="/dev/atom" element={<AtomDevDemo />} />
                            <Route path="/dev/comp" element={<DemoComp />} />
                            <Route path="/dev/theme" element={<DevTheme />} />
                            <Route path="/dev/user-card" element={<DevUserCard />} />
                            <Route path="/dev/contact-card" element={<DevContactCard />} />
                            <Route path="/dev/media" element={<DevMedia />} />
                          </Route>
                          <Route path="/dev/editor" element={<DevEditor />} />
                          <Route path="/dev/instructor" element={<DevInstructor />} />

                          <Route path="/wip" element={<WorkInProgress />} />
                          <Route path="/403" element={<UnauthorizedPage />} />
                          {/* 默认路由：未匹配的路径重定向到首页 */}
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                      </AppContent>
                      <SimpleBottomNavigation sx={{}} />
                      {/* <SimpleBottomNavigation sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} /> */}
                    </Box>
                  </SnackbarProvider>
                </LocalizationProvider>
              </TripProvider>
            </GroupSessionProvider>
          </SessionProvider>
        </UserProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  )
}

export default App
